import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Snackbar,
  List,
  ListItem,
  Chip,
  Pagination,
  IconButton,
  useTheme,
  Tooltip,
} from "@mui/material";
import {
  Info as InfoIcon,
  RemoveRedEye as RemoveRedEyeIcon,
} from "@mui/icons-material";
import { useAuth0 } from "@auth0/auth0-react";
import { uploadFile } from "../../../../apis/common-apis/fileApi";
import { useQuery } from "@apollo/client";
import { GET_EVIDENCE_BY_CHECK_ID } from "../../../../apis/settingsApis/framework-apis/frameworkApis";
import { EvidenceStatus } from "../../../../utils/enums";
import { renderValidationStatusChip } from "../../../Settings/Frameworks/FrameWorkDialogs/Control-Components/ControlComponents";
import BlinkingStatusDot from "../../../../components/BlinkingStatusDot/BlinkingStatusDot";
import UploadFileDialog from "../../../../components/UploadFileDialog/UploadFileDialog";
import EntityDetailDialog from "../../../IntegratedEvidence/EntityDetailDialog";

const passWithEvidenceFileTypes = [
  ".pdf",
  ".doc",
  ".docx",
  ".jpg",
  ".jpeg",
  ".png",
  ".zip",
  ".txt",
  ".xlsx",
  ".xls"
];

const regularCheckFileTypes = [
  ".pdf",
  ".doc",
  ".docx",
  ".jpg",
  ".jpeg",
  ".png",
  ".zip",
  ".txt"
];
const ITEMS_PER_PAGE = 5;

function getFileValidationStatus(fileDetail, theme) {
  const isFileValid = fileDetail.isFileValid;
  if (isFileValid === true) {
    return {
      status: EvidenceStatus?.VALID,
      color: "green",
      dotColor: theme.customColors.status.success.text,
      message: "Acceptable",
      showChecks: true,
    };
  } else if (isFileValid === null) {
    return {
      status: EvidenceStatus?.PROCESSING,
      color: "orange",
      dotColor: theme.customColors.status.warning.alert,
      message: "Processing...",
      showChecks: false,
    };
  } else {
    return {
      status: EvidenceStatus?.INVALID,
      color: "red",
      dotColor: theme.customColors.status.error.text,
      chipStatus: EvidenceStatus?.INVALID,
      message: "Invalid",
      showChecks: false,
    };
  }
}

function getLinkedEvidenceAcceptedStatus(theme) {
  return {
    status: EvidenceStatus?.VALID,
    color: "green",
    dotColor: theme.customColors.status.success.text,
    message: "Acceptable",
    showChecks: true,
  };
}

const EvidenceUpload = ({
  activeCheckId,
  onSubmit,
  isPassWithEvidence = false,
  onRefetchCheckDetails,
}) => {
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const { getAccessTokenSilently, getIdTokenClaims } = useAuth0();
  const theme = useTheme();
  const POLLING_INTERVAL = 2500;
  const [justification, setJustification] = useState("");
  const [pendingValidationFileId, setPendingValidationFileId] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState({ id: null, type: null });
  const {
    data: evidenceData,
    loading: evidenceLoading,
    refetch: refetchEvidence,
    startPolling,
    stopPolling,
  } = useQuery(GET_EVIDENCE_BY_CHECK_ID, {
    variables: { check_id: Number(activeCheckId) },
    fetchPolicy: "network-only",
    pollInterval: POLLING_INTERVAL,
    skip: !activeCheckId,
  });
  const evidenceRows = evidenceData?.getEvidenceByCheckId || [];
  const fileRows = evidenceRows.filter(
    (row) => row.fileId != null
  );

  useEffect(() => {
    if (!evidenceData?.getEvidenceByCheckId) return;
    const hasProcessing = fileRows.some((file) => file.isFileValid === null);

    let timeoutId;

    if (hasProcessing) {
      startPolling(POLLING_INTERVAL);
      timeoutId = setTimeout(() => {
        stopPolling();
      }, 30000);
    } else {
      stopPolling();
    }
    return () => {
      stopPolling();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [evidenceData, fileRows, startPolling, stopPolling]);


  useEffect(() => {
    if (pendingValidationFileId == null || !evidenceData?.getEvidenceByCheckId) {
      return;
    }

    const fileObj = fileRows.find((f) => f.fileId === pendingValidationFileId);
    if (
      !fileObj ||
      fileObj.isFileValid === null ||
      fileObj.isFileValid === undefined
    ) {
      return;
    }

    const isValid = fileObj.isFileValid;
    setPendingValidationFileId(null);
    setSnackbar({
      message: isValid
        ? "Evidence validated successfully!"
        : "File validation failed. Please select a valid file.",
      vertical: "top",
      horizontal: "right",
    });

    if (onRefetchCheckDetails) {
      onRefetchCheckDetails();
    }
  }, [evidenceData, pendingValidationFileId, fileRows, onRefetchCheckDetails]);

  const allowedFileTypes = useMemo(() => {
    return isPassWithEvidence ? passWithEvidenceFileTypes : regularCheckFileTypes;
  }, [isPassWithEvidence]);

  const handleFileUpload = async (file) => {
    try {
      const accessToken = await getAccessTokenSilently();
      const idToken = (await getIdTokenClaims()).__raw;

      const response = await uploadFile(
        file,
        isPassWithEvidence ? "pass_with_evidence" : "check_evidence",
        activeCheckId,
        undefined,
        accessToken,
        idToken,
        true,
        undefined,
        justification
      );

      if (response && response.id) {
        setJustification("");

        if (isPassWithEvidence) {
          setPendingValidationFileId(response.id);
          setSnackbar({
            message: "File uploaded successfully! Waiting for validation...",
            vertical: "top",
            horizontal: "right",
          });
        } else {
          setSnackbar({
            message: "File uploaded successfully! Processing...",
            vertical: "top",
            horizontal: "right",
          });
        }

        refetchEvidence();
        if (onSubmit) onSubmit();
      } else {
        throw new Error("Upload failed");
      }
    } catch (err) {
      setSnackbar({
        message: "Upload failed. Please try again.",
        vertical: "top",
        horizontal: "right",
      });
      throw err;
    }
  };

  const handleOpenUploadDialog = () => {
    setJustification(""); // Reset justification when opening dialog
    setIsUploadDialogOpen(true);
  };

  const totalItems = evidenceRows.length;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const paginatedData = evidenceRows.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handleDownloadDocument = (path, name) => {
    if (path) {
      const link = document.createElement("a");
      link.href = `${process.env?.REACT_APP_CLOUDFRONT_URL || ""}/${path}`;
      link.download = name;
      link.click();
    }
  };

  const openEntityDetail = (entityId, entityType) => {
    setSelectedEntity({ id: entityId, type: entityType });
    setDetailOpen(true);
  };

  return (
    <>
      <Box
        sx={{
          borderRadius: "12px",
          border: `1px solid ${theme.customColors.border.main}`,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          backgroundColor: theme.customColors.background.main,
          boxShadow: theme.customColors.shadow.main,
        }}
      >
        {/* Evidence Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 1.5,
            borderBottom: `1px solid ${theme.customColors.border.main}`,
            backgroundColor: theme.customColors.background.cardHeader
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                fontSize: "1rem",
                color: theme.customColors.text.strong,
              }}
            >
              Evidence
            </Typography>
            <Tooltip title="Evidence files are automatically checked against compliance requirements">
              <InfoIcon fontSize="small" sx={{ color: theme.customColors.text.secondary, cursor: "help" }} />
            </Tooltip>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleOpenUploadDialog}
              sx={{
                textTransform: "none",
                fontSize: "14px",
                fontWeight: 500,
                py: 0.75,
                px: 1.75,
                borderRadius: "8px",
                boxShadow: "none",
                "&:hover": {
                  bgcolor: theme.customColors.button.hover,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }
              }}
            >
              Upload Evidence
            </Button>
          </Box>
        </Box>

        {/* Evidence Content */}
        <Box>
          {evidenceLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                p: 4,
              }}
            >
              <CircularProgress size={32} />
            </Box>
          ) : totalItems === 0 ? (
            <Typography
              variant="body2"
              sx={{
                textAlign: "center",
                padding: "32px",
                color: theme.customColors.text.secondary
              }}
            >
              No Evidence Uploaded
            </Typography>
          ) : (
            <>
              <List sx={{ padding: 0, margin: 0 }}>
                {paginatedData.map((evidenceRow, index) => {
                  const isFileEvidence =
                    evidenceRow.entityType === "file" &&
                    (!!evidenceRow.path || evidenceRow.fileId != null);
                  const isViewableLinkedEvidence =
                    !!evidenceRow.entityId && evidenceRow.entityType !== "file";
                  const validationStatus = getFileValidationStatus(evidenceRow, theme);
                  const linkedAcceptedStatus = getLinkedEvidenceAcceptedStatus(theme);
                  const title =
                    evidenceRow.maskName || evidenceRow.entityName || "Unknown Evidence";
                  const rowNumber = (currentPage - 1) * ITEMS_PER_PAGE + index + 1;

                  return (
                    <ListItem
                      key={`${evidenceRow.entityType || "unknown"}-${evidenceRow.entityId || evidenceRow.fileId || index}-${index}`}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        backgroundColor: index % 2 === 0 ? theme.palette.action.hover : "transparent",
                        p: 2,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 600, color: "#555", marginRight: 2, width: "20px", pt: 0.25 }}
                      >
                        {rowNumber}.
                      </Typography>
                      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                          <Typography
                            variant="body1"
                            onClick={() => isFileEvidence && handleDownloadDocument(evidenceRow.path, title)}
                            sx={{
                              fontWeight: 500,
                              color: isFileEvidence ? "#1976d2" : theme.customColors.text.strong,
                              cursor: isFileEvidence ? "pointer" : "default",
                              "&:hover": isFileEvidence ? { textDecoration: "underline" } : {},
                            }}
                          >
                            {title}
                          </Typography>
                          {!isFileEvidence && isViewableLinkedEvidence && (
                            <Tooltip title={`View ${evidenceRow.entityType}`} arrow>
                              <IconButton
                                size="small"
                                onClick={() => openEntityDetail(evidenceRow.entityId, evidenceRow.entityType)}
                                sx={{ color: "#031B89", p: 0.25 }}
                              >
                                <RemoveRedEyeIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                        <Typography variant="caption" color="textSecondary">
                          {isFileEvidence
                            ? `File Status: ${validationStatus.status}`
                            : `${(evidenceRow.entityType || "evidence").toUpperCase()} evidence`}
                        </Typography>

                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 2 }}>
                        {isFileEvidence ? (
                          <>
                            {renderValidationStatusChip(validationStatus)}
                            <BlinkingStatusDot status={validationStatus.status} />
                          </>
                        ) : (
                          <>
                            {renderValidationStatusChip(linkedAcceptedStatus)}
                            <BlinkingStatusDot status={linkedAcceptedStatus.status} />
                          </>
                        )}
                      </Box>
                    </ListItem>
                  );
                })}
              </List>

              {/* Pagination Component */}
              {totalPages > 1 && (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  paddingTop={2}
                  paddingBottom={2}
                >
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={(_, value) => setCurrentPage(value)}
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      <UploadFileDialog
        open={isUploadDialogOpen}
        onClose={() => {
          setIsUploadDialogOpen(false);
          setJustification(""); // Reset justification on close
        }}
        onFileUpload={handleFileUpload}
        allowedFileTypes={allowedFileTypes}
        dialogTitle="Upload Evidence File"
        uploadButtonText="Upload"
        showJustification={isPassWithEvidence}
        justification={justification}
        onJustificationChange={setJustification}
      />

      <EntityDetailDialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        entityId={selectedEntity.id}
        entityType={selectedEntity.type}
        contextCheckId={activeCheckId}
        onActionComplete={() => refetchEvidence()}
      />

      {snackbar && (
        <Snackbar
          open={!!snackbar}
          autoHideDuration={6000}
          onClose={() => setSnackbar(null)}
          message={snackbar.message}
          anchorOrigin={snackbar.vertical && snackbar.horizontal ? { vertical: snackbar.vertical, horizontal: snackbar.horizontal } : { vertical: "bottom", horizontal: "center" }}
        />
      )}
    </>
  );
};

export default EvidenceUpload;
